"use client";

import {
  AccountBalanceOutlined,
  DescriptionOutlined,
  ArrowForward,
  SupportAgentOutlined,
  LinkOutlined,
  PaymentsOutlined,
  SyncAltOutlined,
  ApiOutlined,
  CampaignOutlined,
  FactCheckOutlined,
  ReceiptLongOutlined,
  SchoolOutlined,
  QrCode2Outlined,
  VerifiedOutlined,
} from "@mui/icons-material";
import { Box, Card, Container, Typography } from "@mui/material";
import Image from "next/image";
import { ActionButton, CardGrid, CtaBand, Section, Steps, TextLink } from "../components/Sections";
import { StoreBadges } from "../components/StoreBadges";
import { useDocumentTitle, useLanguage } from "../components/useLanguage";
import { SNAPNPAY, SNAPNPAY_COUNCILS, SNAPNPAY_KPTM, SNAPNPAY_KPTM_CAMPUSES, SNAPNPAY_KPTM_PDFS, SNAPNPAY_KPTM_SOURCE, SNAPNPAY_LINKS } from "../content/snapnpay";
import { TULUS } from "../brand";
// SnapNPay's own store images (see content/snapnpay.ts), shown as published.
import Logo from "../../public/images/snapnpay/snapnpay-logo.webp";
import ScreenScan from "../../public/images/snapnpay/screen-scan.webp";
import ScreenSignIn from "../../public/images/snapnpay/screen-sign-in.webp";
import ScreenCouncils from "../../public/images/snapnpay/screen-councils.webp";

const EVERYDAY_ICONS = [AccountBalanceOutlined, SchoolOutlined, ReceiptLongOutlined];
const ORG_ICONS = [QrCode2Outlined, ApiOutlined, VerifiedOutlined, FactCheckOutlined];
const KPTM_VALUE_ICONS = [PaymentsOutlined, LinkOutlined, SupportAgentOutlined, SyncAltOutlined];
const JOURNEY_SCREENS = [ScreenSignIn, ScreenCouncils];

/** SnapNPay, a Tulus Digital payment product (content/snapnpay.ts). */
export default function SnapNPayView() {
  const [lang] = useLanguage();
  const c = SNAPNPAY[lang];
  useDocumentTitle(c.meta.title);
  return (
    <>
      <Box component="section" sx={{ backgroundColor: TULUS.mist, borderBottom: `1px solid ${TULUS.line}` }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 }, display: "grid", gap: { xs: 4, md: 6 }, gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 300px" }, alignItems: "center" }}>
          <Box sx={{ maxWidth: 760 }}>
            <Image src={Logo} alt={c.hero.logoAlt} priority sizes="220px" style={{ width: 220, height: "auto", display: "block", marginBottom: 20 }} />
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: TULUS.cyanText, mb: 1.5 }}>
              {c.hero.eyebrow}
            </Typography>
            <Typography variant="h1" sx={{ fontSize: { xs: "2.1rem", md: "3rem" }, lineHeight: 1.15, color: TULUS.ink, textWrap: "balance", mb: 2.5 }}>
              {c.hero.title}
            </Typography>
            {c.hero.lead.map((p) => (
              <Typography key={p} sx={{ fontSize: { xs: "1.02rem", md: "1.12rem" }, color: TULUS.ink, lineHeight: 1.7, mb: 2, maxWidth: 680 }}>
                {p}
              </Typography>
            ))}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3.5 }}>
              {c.hero.badges.map((b) => (
                <Box key={b} component="span" sx={{ px: 1.5, py: 0.5, borderRadius: "999px", backgroundColor: TULUS.paper, border: `1px solid ${TULUS.line}`, fontSize: "0.9rem", color: TULUS.ink }}>
                  {b}
                </Box>
              ))}
            </Box>
            <StoreBadges appStore={{ href: SNAPNPAY_LINKS.appStore, label: c.hero.appStore }} googlePlay={{ href: SNAPNPAY_LINKS.googlePlay, label: c.hero.googlePlay }} />
            <Box sx={{ mt: 2 }}>
              <ActionButton action={{ label: c.hero.organisation, href: "/hubungi-kami" }} variant="outlined" />
            </Box>
            <Typography sx={{ mt: 1.5, fontSize: "0.92rem", color: TULUS.mutedText }}>{c.hero.stores}</Typography>
          </Box>
          <Box sx={{ justifySelf: "center", width: { xs: 240, md: 300 } }}>
            <Image src={ScreenScan} alt={c.hero.screenAlt} priority sizes="300px" style={{ width: "100%", height: "auto", borderRadius: 18, boxShadow: TULUS.shadow }} />
          </Box>
        </Container>
      </Box>

      <Section title={c.journey.title} intro={[c.journey.intro]}>
        <Box sx={{ display: "grid", gap: { xs: 4, md: 6 }, gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" }, alignItems: "center" }}>
          <Steps items={c.journey.steps} />
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            {c.journey.screens.map((sc, i) => (
              <Box key={sc.alt} component="figure" sx={{ m: 0, width: { xs: 150, sm: 200 } }}>
                <Image src={JOURNEY_SCREENS[i]} alt={sc.alt} sizes="200px" style={{ width: "100%", height: "auto", borderRadius: 14, boxShadow: TULUS.shadow }} />
                <Typography component="figcaption" sx={{ mt: 1, fontSize: "0.9rem", color: TULUS.mutedText, textAlign: "center" }}>{sc.caption}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Section>

      <Section title={c.everyday.title} tone="mist">
        <CardGrid items={c.everyday.items.map((it, i) => ({ ...it, icon: EVERYDAY_ICONS[i] }))} />
      </Section>

      <Section title={c.councils.title} intro={[c.councils.intro]}>
        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" } }}>
          {SNAPNPAY_COUNCILS.map((co) => (
            <Card key={co.short} component="li" sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 2 }}>
              {/* The council's own crest; its name is printed beside it, so the image is decorative. */}
              <Box sx={{ position: "relative", width: 64, height: 64, flexShrink: 0 }}>
                <Image src={`/images/snapnpay/councils/${co.short.toLowerCase()}.webp`} alt="" fill sizes="64px" style={{ objectFit: "contain" }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 600, color: TULUS.cyanText, fontSize: "0.88rem" }}>{co.short}</Typography>
                <Typography sx={{ color: TULUS.ink, fontSize: "0.97rem", lineHeight: 1.45 }}>{co.name}</Typography>
              </Box>
            </Card>
          ))}
        </Box>
      </Section>


      {/* KPTM case study (content/snapnpay.ts, from the KPTM handoff). */}
      <Section id="kptm" tone="mist">
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap", mb: 3 }}>
          <Box sx={{ position: "relative", width: 150, height: 74, backgroundColor: TULUS.paper, borderRadius: "12px", border: `1px solid ${TULUS.line}`, flexShrink: 0 }}>
            <Image src="/images/snapnpay/councils/kptm.webp" alt={SNAPNPAY_KPTM.name} fill sizes="150px" style={{ objectFit: "contain", padding: 10 }} />
          </Box>
          <Box sx={{ flex: "1 1 320px" }}>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: TULUS.cyanText, mb: 0.5 }}>
              {c.kptm.eyebrow} · {SNAPNPAY_KPTM.short}
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "1.9rem" }, color: TULUS.ink, textWrap: "balance" }}>{c.kptm.title}</Typography>
          </Box>
        </Box>
        <Typography sx={{ fontSize: { xs: "1.05rem", md: "1.15rem" }, color: TULUS.ink, lineHeight: 1.7, maxWidth: 760, mb: 5 }}>{c.kptm.lead}</Typography>

        <Box sx={{ display: "grid", gap: { xs: 4, md: 6 }, gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, mb: 6 }}>
          <Box>
            <Typography variant="h3" sx={{ fontSize: "1.15rem", color: TULUS.ink, mb: 1.5 }}>{c.kptm.aboutTitle}</Typography>
            <Typography sx={{ color: TULUS.ink, lineHeight: 1.75, mb: 2 }}>{c.kptm.about}</Typography>
            <Typography sx={{ color: TULUS.ink, lineHeight: 1.75 }}>
              {c.kptm.population} (
              <Box component="a" href={SNAPNPAY_KPTM_SOURCE} target="_blank" rel="noopener noreferrer" sx={{ color: TULUS.cyanText }}>
                {c.kptm.populationSource}
              </Box>
              ).
            </Typography>
          </Box>
          <Box>
            <Typography variant="h3" sx={{ fontSize: "1.15rem", color: TULUS.ink, mb: 1.5 }}>{c.kptm.journeyTitle}</Typography>
            <Steps items={c.kptm.journey} />
          </Box>
        </Box>

        <Typography variant="h3" sx={{ fontSize: "1.15rem", color: TULUS.ink, mb: 2 }}>{c.kptm.flowLabel}</Typography>
        <Box component="ol" aria-label={c.kptm.flowLabel} sx={{ listStyle: "none", p: 0, m: 0, mb: 6, display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "stretch", gap: 1 }}>
          {c.kptm.flow.map((step, i) => (
            <Box key={step} component="li" sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", gap: 1, flex: 1 }}>
              <Card sx={{ px: 2, py: 1.75, flex: 1, width: "100%", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 72 }}>
                <Typography sx={{ color: TULUS.ink, fontWeight: 600, fontSize: "0.97rem", lineHeight: 1.4 }}>{step}</Typography>
              </Card>
              {i < c.kptm.flow.length - 1 && <ArrowForward aria-hidden sx={{ color: TULUS.cyan, transform: { xs: "rotate(90deg)", md: "none" } }} />}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "grid", gap: { xs: 4, md: 6 }, gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, mb: 6 }}>
          <Box>
            <Typography variant="h3" sx={{ fontSize: "1.15rem", color: TULUS.ink, mb: 1.5 }}>{c.kptm.solutionTitle}</Typography>
            {c.kptm.solution.map((p) => (
              <Typography key={p} sx={{ color: TULUS.ink, lineHeight: 1.75, mb: 2 }}>{p}</Typography>
            ))}
          </Box>
          <Box>
            <Typography variant="h3" sx={{ fontSize: "1.15rem", color: TULUS.ink, mb: 1.5 }}>{c.kptm.campusesLabel}</Typography>
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexWrap: "wrap", gap: 1 }}>
              {SNAPNPAY_KPTM_CAMPUSES.map((campus) => (
                <Box key={campus} component="li" sx={{ px: 1.75, py: 0.75, borderRadius: "999px", backgroundColor: TULUS.paper, border: `1px solid ${TULUS.line}`, fontSize: "0.95rem", color: TULUS.ink }}>
                  {campus}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Typography variant="h3" sx={{ fontSize: "1.15rem", color: TULUS.ink, mb: 2 }}>{c.kptm.valueTitle}</Typography>
        <CardGrid items={c.kptm.value.map((it, i) => ({ ...it, icon: KPTM_VALUE_ICONS[i] }))} columns={2} />
        <Box sx={{ mt: 4 }}>
          <ActionButton action={{ label: c.kptm.action, href: "/hubungi-kami" }} />
          <Box sx={{ mt: 3, display: "flex", flexWrap: "wrap", alignItems: "center", gap: { xs: 1, sm: 2.5 } }}>
            <Typography sx={{ color: TULUS.ink, fontWeight: 500 }}>{c.kptm.downloads}:</Typography>
            {([["en", c.kptm.pdfEn], ["ms", c.kptm.pdfMs]] as const).map(([k, label]) => (
              <Box
                key={k}
                component="a"
                href={SNAPNPAY_KPTM_PDFS[k]}
                target="_blank"
                rel="noopener"
                hrefLang={k}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minHeight: 44, color: TULUS.cyanText, fontWeight: 500, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                <DescriptionOutlined sx={{ fontSize: 20 }} aria-hidden />
                {label}
              </Box>
            ))}
          </Box>
        </Box>
      </Section>

      <Section title={c.organisations.title} intro={[c.organisations.intro]}>
        <CardGrid items={c.organisations.items.map((it, i) => ({ ...it, icon: ORG_ICONS[i] }))} columns={2} />
        <Box sx={{ mt: 4 }}>
          <ActionButton action={{ label: c.organisations.action, href: "/hubungi-kami" }} />
        </Box>
      </Section>

      <Section>
        <Card component="aside" sx={{ p: { xs: 3, md: 4 }, display: "flex", gap: 2.5, flexDirection: { xs: "column", sm: "row" }, alignItems: { sm: "center" }, borderLeft: `4px solid ${TULUS.cyan}` }}>
          <CampaignOutlined aria-hidden sx={{ fontSize: 44, color: TULUS.cyan }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h2" sx={{ fontSize: "1.35rem", color: TULUS.ink, mb: 1 }}>{c.snapnshare.title}</Typography>
            <Typography sx={{ color: TULUS.ink, lineHeight: 1.7 }}>{c.snapnshare.body}</Typography>
          </Box>
          <ActionButton action={{ label: c.snapnshare.action, href: "/hubungi-kami" }} variant="outlined" />
        </Card>
      </Section>

      <Section title={c.tulus.title} intro={[c.tulus.body]} tone="mist">
        <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 4, rowGap: 1 }}>
          <TextLink action={{ label: c.tulus.gateway, href: "/payment-gateway" }} />
          <TextLink action={{ label: c.tulus.link, href: "/penyelesaian" }} />
        </Box>
      </Section>

      <CtaBand title={c.closing.title} body={c.closing.body} action={{ label: c.closing.action, href: "/hubungi-kami" }} />
    </>
  );
}
